import React, { useState, useEffect } from "react";

// Importação corrigida da logo
const logo = new URL("../assets/logo.png", import.meta.url).href;

interface NavItem {
  label: string;
  id: string;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);

  // Detectar scroll para adicionar efeitos
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevenir scroll quando menu está aberto
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [menuOpen]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const navItems: NavItem[] = [
    { label: "Início", id: "home" },
    { label: "Serviços", id: "servicos" },
    { label: "Sobre", id: "sobre" },
    { label: "Contato", id: "contato" },
  ];

  return (
    <>
      {/* Header Desktop e Mobile */}
      <header
        className={`bg-gradient-to-r from-sd-dark via-gray-900 to-sd-dark text-white fixed w-full z-50 top-0 transition-all duration-300 border-b border-white/10 ${
          scrolled
            ? "shadow-2xl py-2 md:py-2.5 backdrop-blur-md"
            : "shadow-xl py-3 md:py-4"
        }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          {/* Logo */}
          <button
            onClick={() => scrollToSection("home")}
            className="flex items-center space-x-3 flex-shrink-0 group cursor-pointer"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-sd-orange/30 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <img
                src={logo}
                alt="SD Serviços Logo"
                className={`relative object-contain transition-all duration-300 ${
                  scrolled
                    ? "w-16 h-16 sm:w-14 sm:h-14 md:w-16 md:h-16"
                    : "w-20 h-20 sm:w-16 sm:h-16 md:w-20 md:h-20"
                } group-hover:scale-110 drop-shadow-[0_10px_20px_rgba(251,146,60,0.3)]`}
              />
            </div>

            <div className="hidden sm:block">
              <span className="font-bold text-lg md:text-xl lg:text-2xl text-white group-hover:text-sd-orange transition-colors duration-300">
                SD Serviços
              </span>
              <div className="text-[10px] md:text-xs text-gray-400 group-hover:text-gray-300 transition-colors duration-300 -mt-0.5 md:-mt-1">
                Manutenções
              </div>
            </div>
          </button>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-300 font-semibold hover:text-sd-orange transition-colors duration-200 relative group py-2"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-sd-orange group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}

            <a
              href="https://wa.me/5548999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-sd-orange text-white px-6 py-2.5 rounded-full font-bold hover:bg-orange-600 hover:shadow-lg hover:shadow-sd-orange/50 hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Contato
            </a>
          </nav>

          {/* Mobile Menu Button - Z-INDEX ALTO */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 relative z-[60]"
            aria-label="Menu"
          >
            <span
              className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-white transition-all duration-300 my-1.5 ${
                menuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            ></span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - Z-INDEX CORRETO */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[45] md:hidden transition-opacity duration-300 ${
          menuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile Sidebar - Z-INDEX ABAIXO DO OVERLAY */}
      <nav
        className={`fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-gradient-to-b from-sd-dark to-gray-900 text-white z-[50] md:hidden transition-transform duration-300 ease-out shadow-2xl ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Header do Menu */}
          <div className="p-6 border-b border-white/10">
            {/* Logo no Sidebar */}
            <div className="flex flex-col items-center space-y-4">
              <img
                src={logo}
                alt="SD Serviços Logo"
                className="w-24 h-24 object-contain drop-shadow-[0_15px_30px_rgba(251,146,60,0.4)]"
              />
              <div className="text-center">
                <div className="font-bold text-xl">SD Serviços</div>
                <div className="text-sm text-gray-400">Manutenções</div>
              </div>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="flex-1 px-4 py-6 space-y-2">
            {navItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="w-full text-left px-6 py-4 rounded-xl text-white font-semibold hover:bg-sd-orange/90 transition-all duration-200 flex items-center justify-between group"
              >
                <span>{item.label}</span>
                <svg
                  className="w-5 h-5 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ))}
          </div>

          {/* Footer do Menu com CTA */}
          <div className="p-6 border-t border-white/10 space-y-4">
            <a
              href="https://wa.me/5548999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-sd-orange text-white px-6 py-4 rounded-xl font-bold hover:bg-orange-600 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-sd-orange/50"
              onClick={() => setMenuOpen(false)}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              WhatsApp
            </a>

            <div className="text-center text-xs text-gray-400">
              © 2024 SD Serviços. Todos os direitos reservados.
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? "h-20 md:h-20" : "h-24 md:h-28"
        }`}
      ></div>
    </>
  );
}

export default Header;
